import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { ROTI_COMMENT_MAX_LENGTH, ROTI_SCALE_LABELS, type RotiView } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { toast } from '../../lib/toast.js';
import { Modal } from '../ui.js';

/**
 * Return On Time Invested.
 *
 * Ratings and written feedback are anonymous permanently — the server strips
 * identity before sending results — which is what makes people willing to say a
 * session was not worth their time.
 */
export function RotiPanel({ roti }: { roti: RotiView }) {
  const send = useRetroStore((state) => state.send);
  const [rating, setRating] = useState(false);
  const [results, setResults] = useState(false);
  // Follow the broadcast rather than initialising once, so "Change my rating"
  // always shows the value the server actually stored.
  const [score, setScore] = useState<number | null>(roti.myScore);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setScore(roti.myScore);
  }, [roti.myScore]);

  async function submit() {
    if (!score || saving) return;
    setSaving(true);
    const result = await send('roti:submit', { score, comment: comment.trim() });
    setSaving(false);
    if (result !== null) {
      setRating(false);
      setComment('');
      toast.info('Rating saved.');
    }
  }

  const max = Math.max(1, ...Object.values(roti.distribution));

  return (
    <>
      <section className="card flex flex-wrap items-center gap-4 p-4">
        <div
          className="flex h-20 w-28 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-2xl"
          aria-hidden
        >
          ⭐⭐⭐⭐
        </div>

        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">ROTI (Return On Time Invested)</h2>
          <p className="mt-0.5 text-sm text-ink/60">
            Rate this retrospective and share anonymous feedback with your team.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" className="btn btn-primary" onClick={() => setRating(true)}>
              {roti.hasSubmitted ? 'Change my rating' : 'Rate this retro'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setResults(true)}
              disabled={!roti.resultsVisible}
              title={roti.resultsVisible ? undefined : 'Rate the session to see the results'}
            >
              View results
            </button>
            <span className="ml-auto text-sm text-ink/55">
              {roti.responseCount} {roti.responseCount === 1 ? 'review' : 'reviews'}
              {roti.average !== null ? ` · avg ${roti.average}` : ''}
            </span>
          </div>
        </div>
      </section>

      <Modal open={rating} onClose={() => setRating(false)} title="Improve your retrospectives">
        <p className="mb-4 text-sm text-ink/60">
          Rate this retrospective from 1 to 5. Your answer stays anonymous, permanently.
        </p>

        <ul className="space-y-1.5">
          {[1, 2, 3, 4, 5].map((value) => {
            const label = ROTI_SCALE_LABELS[value]!;
            const selected = score === value;
            return (
              <li key={value}>
                <button
                  type="button"
                  onClick={() => setScore(value)}
                  className={clsx(
                    'flex w-full items-start gap-3 rounded-xl border p-3 text-left transition',
                    selected ? 'border-brand-500 bg-brand-50' : 'border-black/10 hover:bg-black/[0.03]',
                  )}
                  aria-pressed={selected}
                >
                  <span className="shrink-0 text-sm font-semibold tabular-nums">{value}/5</span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{label.title}</span>
                    <span className="block text-sm text-ink/60">{label.detail}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <label className="mt-4 block">
          <span className="block text-[13px] font-semibold">Anything to add? (optional)</span>
          <textarea
            className="input min-h-[5rem] resize-y"
            value={comment}
            maxLength={ROTI_COMMENT_MAX_LENGTH}
            onChange={(event) => setComment(event.target.value)}
            placeholder="What would make the next retrospective better?"
          />
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn" onClick={() => setRating(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={!score || saving}>
            {saving ? 'Sending…' : 'Submit rating'}
          </button>
        </div>
      </Modal>

      <Modal open={results} onClose={() => setResults(false)} title="ROTI results">
        {roti.responseCount === 0 ? (
          <p className="text-sm text-ink/60">Nobody has rated this retrospective yet.</p>
        ) : (
          <>
            <p className="mb-4 text-sm text-ink/60">
              Average <span className="font-semibold text-ink">{roti.average}</span> from{' '}
              {roti.responseCount} {roti.responseCount === 1 ? 'person' : 'people'}.
            </p>

            <ul className="mb-5 space-y-1.5">
              {[5, 4, 3, 2, 1].map((value) => {
                const count = roti.distribution[String(value)] ?? 0;
                return (
                  <li key={value} className="flex items-center gap-2 text-sm">
                    <span className="w-6 tabular-nums text-ink/60">{value}</span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
                      <span
                        className="block h-full rounded-full bg-brand-500"
                        style={{ width: `${(count / max) * 100}%` }}
                      />
                    </span>
                    <span className="w-6 text-right tabular-nums text-ink/60">{count}</span>
                  </li>
                );
              })}
            </ul>

            {roti.comments.length > 0 ? (
              <>
                <h3 className="mb-2 text-sm font-semibold">Anonymous feedback</h3>
                <ul className="space-y-2">
                  {roti.comments.map((entry, index) => (
                    <li key={index} className="rounded-xl bg-black/[0.03] p-3">
                      <p className="text-xs font-semibold text-ink/50">{entry.score}/5</p>
                      <p className="mt-0.5 whitespace-pre-wrap text-sm">{entry.comment}</p>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}
      </Modal>
    </>
  );
}
